// Medical Scores Database - Complete Collection Part 8
// Final batch of specialized scores

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Target,
  Shield,
  Zap,
  Eye
} from 'lucide-react';

export const SCORES_PART_8 = {
  // ASPECTS - AVC ischémique
  aspects: {
    name: 'Score ASPECTS',
    category: 'Neurologie',
    icon: Brain,
    description: 'Extension infarctus ACM au scanner',
    color: 'purple',
    fields: [
      { id: 'c', label: 'Noyau caudé (C)', type: 'select', options: [
        { value: 1, label: 'Normal (1 pt)' }, { value: 0, label: 'Atteint (0 pt)' }
      ]},
      { id: 'l', label: 'Noyau lenticulaire (L)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'ic', label: 'Capsule interne (IC)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'i', label: 'Ruban insulaire (I)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'm1', label: 'ACM antérieure (M1)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'm2', label: 'ACM latérale au ruban insulaire (M2)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'm3', label: 'ACM postérieure (M3)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'm4', label: 'ACM ant sup (M4)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'm5', label: 'ACM lat sup (M5)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]},
      { id: 'm6', label: 'ACM post sup (M6)', type: 'select', options: [
        { value: 1, label: 'Normal' }, { value: 0, label: 'Atteint' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score >= 8) return { level: 'success', text: `ASPECTS ${score}/10`, detail: 'Petit infarctus - Bon candidat thrombolyse/thrombectomie' };
      if (score >= 6) return { level: 'warning', text: `ASPECTS ${score}/10`, detail: 'Infarctus modéré - Discussion au cas par cas' };
      return { level: 'danger', text: `ASPECTS ${score}/10`, detail: 'Grand infarctus - Bénéfice thrombolyse incertain, risque hémorragique' };
    },
    formula: 'ASPECTS: 10 régions ACM. Score ≥7 pour thrombolyse, ≥6 pour thrombectomie'
  },

  // APRI - Fibrose hépatique
  apri: {
    name: 'Score APRI',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Fibrose hépatique (hépatite C)',
    color: 'yellow',
    fields: [
      { id: 'ast', label: 'ASAT (UI/L)', type: 'number', min: 5, max: 1000 },
      { id: 'astUln', label: 'Limite sup normale ASAT (UI/L)', type: 'number', min: 20, max: 60 },
      { id: 'platelets', label: 'Plaquettes (G/L)', type: 'number', min: 20, max: 600 }
    ],
    calculate: (values) => {
      const ast = values.ast || 30;
      const astUln = values.astUln || 40;
      const plt = values.platelets || 200;
      const apri = ((ast / astUln) / plt) * 100;
      return { score: apri.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const a = parseFloat(score);
      if (a < 0.5) return { level: 'success', text: `APRI ${score}`, detail: 'Fibrose significative peu probable (VPN 86%)' };
      if (a < 1.5) return { level: 'warning', text: `APRI ${score}`, detail: 'Zone indéterminée - Fibroscan recommandé' };
      return { level: 'danger', text: `APRI ${score}`, detail: 'Fibrose significative/cirrhose probable (VPP 88%)' };
    },
    formula: 'APRI = (ASAT/LSN) / Plaquettes × 100'
  },

  // Beers Criteria (simplifié)
  beers: {
    name: 'Critères de Beers (simplifié)',
    category: 'Gériatrie',
    icon: Pill,
    description: 'Médicaments potentiellement inappropriés chez le sujet âgé',
    color: 'amber',
    fields: [
      { id: 'benzoLong', label: 'Benzodiazépines longue durée d\'action', type: 'checkbox', points: 1 },
      { id: 'anticholinergic', label: 'Anticholinergiques (antihistaminiques H1, etc)', type: 'checkbox', points: 1 },
      { id: 'nsaid', label: 'AINS au long cours', type: 'checkbox', points: 1 },
      { id: 'tricyclic', label: 'Antidépresseurs tricycliques', type: 'checkbox', points: 1 },
      { id: 'muscle', label: 'Myorelaxants', type: 'checkbox', points: 1 },
      { id: 'digoxin', label: 'Digoxine > 0.125 mg/j', type: 'checkbox', points: 1 },
      { id: 'hypnotics', label: 'Hypnotiques au long cours', type: 'checkbox', points: 1 },
      { id: 'antipsychotics', label: 'Antipsychotiques (si démence)', type: 'checkbox', points: 1 },
      { id: 'protonPump', label: 'IPP > 8 semaines sans indication', type: 'checkbox', points: 1 },
      { id: 'alpha1', label: 'Alpha-1 bloquants (si incontinence)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Beers 0', detail: 'Pas de médicament potentiellement inapproprié détecté' };
      if (score <= 2) return { level: 'warning', text: `Beers ${score}`, detail: 'Réévaluer l\'indication des médicaments listés' };
      return { level: 'danger', text: `Beers ${score}`, detail: 'Polymorphisme médicamenteux inapproprié - Révision urgente' };
    },
    formula: 'Beers: liste AGS 2019 simplifiée'
  },

  // Boey - Ulcère perforé
  boey: {
    name: 'Score de Boey',
    category: 'Chirurgie',
    icon: Stethoscope,
    description: 'Pronostic ulcère gastroduodénal perforé',
    color: 'stone',
    fields: [
      { id: 'shock', label: 'Choc à l\'admission (PAS < 90)', type: 'checkbox', points: 1 },
      { id: 'comorbidity', label: 'Comorbidités sévères (ASA ≥3)', type: 'checkbox', points: 1 },
      { id: 'delay', label: 'Délai chirurgie > 24h', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.shock) total += 1;
      if (values.comorbidity) total += 1;
      if (values.delay) total += 1;
      return { score: total, max: 3 };
    },
    interpret: (score) => {
      const mortality = { 0: 0, 1: 10, 2: 45.5, 3: 100 };
      if (score === 0) return { level: 'success', text: `Boey ${score}`, detail: 'Mortalité nulle' };
      if (score === 1) return { level: 'warning', text: `Boey ${score}`, detail: 'Mortalité ~10%' };
      return { level: 'danger', text: `Boey ${score}`, detail: `Mortalité ~${mortality[score]}%` };
    },
    formula: 'Boey: Choc + Comorbidités + Délai >24h'
  },

  // CURB-65 complété déjà

  // Cockcroft-Gault
  cockcroftGault: {
    name: 'Cockcroft-Gault',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'Clairance créatinine',
    color: 'blue',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 110 },
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 200 },
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 20, max: 2000 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 1, label: 'Homme' },
        { value: 0.85, label: 'Femme' }
      ]}
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const weight = values.weight || 70;
      const cr = values.creatinine || 100;
      const sex = values.sex || 1;
      
      const clcr = ((140 - age) * weight * sex) / (0.814 * cr);
      return { score: clcr.toFixed(0), max: '', unit: 'mL/min' };
    },
    interpret: (score) => {
      const cl = parseInt(score);
      if (cl >= 90) return { level: 'success', text: `ClCr ${score} mL/min`, detail: 'Fonction rénale normale' };
      if (cl >= 60) return { level: 'success', text: `ClCr ${score} mL/min`, detail: 'IRC légère' };
      if (cl >= 30) return { level: 'warning', text: `ClCr ${score} mL/min`, detail: 'IRC modérée - Adaptation posologique' };
      if (cl >= 15) return { level: 'danger', text: `ClCr ${score} mL/min`, detail: 'IRC sévère' };
      return { level: 'danger', text: `ClCr ${score} mL/min`, detail: 'IRC terminale' };
    },
    formula: 'ClCr = [(140-âge) × poids × k] / (0.814 × Cr) avec k=1(H) ou 0.85(F)'
  },

  // CHADS-VASc (déjà présent mais renforcé)
  chadsVascExtended: {
    name: 'CHA₂DS₂-VASc (complet)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque embolique FA avec recommandations',
    color: 'rose',
    fields: [
      { id: 'chf', label: 'C - Insuffisance cardiaque/FEVG ≤40%', type: 'checkbox', points: 1 },
      { id: 'htn', label: 'H - HTA', type: 'checkbox', points: 1 },
      { id: 'age75', label: 'A₂ - Âge ≥75 ans', type: 'checkbox', points: 2 },
      { id: 'dm', label: 'D - Diabète', type: 'checkbox', points: 1 },
      { id: 'stroke', label: 'S₂ - AVC/AIT/embolie', type: 'checkbox', points: 2 },
      { id: 'vascular', label: 'V - Maladie vasculaire (IDM, AOMI, plaque Ao)', type: 'checkbox', points: 1 },
      { id: 'age65', label: 'A - Âge 65-74 ans', type: 'checkbox', points: 1 },
      { id: 'female', label: 'Sc - Sexe féminin', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.chf) total += 1;
      if (values.htn) total += 1;
      if (values.age75) total += 2;
      if (values.dm) total += 1;
      if (values.stroke) total += 2;
      if (values.vascular) total += 1;
      if (values.age65 && !values.age75) total += 1;
      if (values.female) total += 1;
      return { score: total, max: 9 };
    },
    interpret: (score, calculated, values) => {
      const isFemale = values.female && score === 1;
      if (score === 0) return { level: 'success', text: `CHA₂DS₂-VASc ${score}`, detail: 'Faible risque - Anticoagulation non recommandée (Homme)' };
      if (isFemale) return { level: 'success', text: `CHA₂DS₂-VASc ${score}`, detail: 'Femme sans autre FR - Anticoagulation non recommandée' };
      if (score === 1) return { level: 'warning', text: `CHA₂DS₂-VASc ${score}`, detail: 'Anticoagulation à considérer (Homme)' };
      return { level: 'danger', text: `CHA₂DS₂-VASc ${score}`, detail: `Risque élevé (~${(score * 1.5).toFixed(1)}%/an) - Anticoagulation recommandée` };
    },
    formula: 'CHA₂DS₂-VASc: ≥2 (H) ou ≥3 (F) = anticoagulation'
  },

  // DeMeester - RGO
  demeester: {
    name: 'Score de DeMeester',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Diagnostic RGO par pH-métrie',
    color: 'yellow',
    fields: [
      { id: 'totalTime', label: '% temps total pH<4', type: 'number', min: 0, max: 100, step: 0.1 },
      { id: 'uprightTime', label: '% temps debout pH<4', type: 'number', min: 0, max: 100, step: 0.1 },
      { id: 'supineTime', label: '% temps couché pH<4', type: 'number', min: 0, max: 100, step: 0.1 },
      { id: 'episodes', label: 'Nombre total épisodes', type: 'number', min: 0, max: 500 },
      { id: 'episodes5', label: 'Nombre épisodes > 5 min', type: 'number', min: 0, max: 100 },
      { id: 'longest', label: 'Épisode le plus long (min)', type: 'number', min: 0, max: 300 }
    ],
    calculate: (values) => {
      // Formule de DeMeester simplifiée
      const t = values.totalTime || 0;
      const u = values.uprightTime || 0;
      const s = values.supineTime || 0;
      const n = values.episodes || 0;
      const n5 = values.episodes5 || 0;
      const l = values.longest || 0;
      
      // Calcul composite (simplifié)
      const score = (t * 4.93) + (u * 4.85) + (s * 3.53) + (n * 0.14) + (n5 * 4.87) + (l * 0.71);
      return { score: score.toFixed(1), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 14.72) return { level: 'success', text: `DeMeester ${score}`, detail: 'pH-métrie normale' };
      if (s < 50) return { level: 'warning', text: `DeMeester ${score}`, detail: 'RGO léger à modéré' };
      return { level: 'danger', text: `DeMeester ${score}`, detail: 'RGO sévère' };
    },
    formula: 'DeMeester > 14.72 = RGO pathologique'
  },

  // Euroscore II (simplifié)
  euroscoreSimple: {
    name: 'EuroSCORE II (simplifié)',
    category: 'Chirurgie cardiaque',
    icon: Heart,
    description: 'Mortalité chirurgie cardiaque',
    color: 'rose',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'female', label: 'Sexe féminin', type: 'checkbox', points: 0 },
      { id: 'renal', label: 'Créatinine élevée ou dialyse', type: 'select', options: [
        { value: 0, label: 'Normale' },
        { value: 1, label: 'Modérément élevée' },
        { value: 2, label: 'Sévèrement élevée ou dialyse' }
      ]},
      { id: 'extracardiac', label: 'Artheriopathie extracardiaque', type: 'checkbox', points: 1 },
      { id: 'poorMobility', label: 'Mobilité réduite', type: 'checkbox', points: 1 },
      { id: 'cardiac', label: 'Chirurgie cardiaque antérieure', type: 'checkbox', points: 1 },
      { id: 'pulmonary', label: 'Maladie pulmonaire chronique', type: 'checkbox', points: 1 },
      { id: 'endocarditis', label: 'Endocardite active', type: 'checkbox', points: 1 },
      { id: 'critical', label: 'État critique pré-op', type: 'checkbox', points: 2 },
      { id: 'lvef', label: 'FEVG', type: 'select', options: [
        { value: 0, label: '> 50%' },
        { value: 1, label: '31-50%' },
        { value: 2, label: '21-30%' },
        { value: 3, label: '≤ 20%' }
      ]},
      { id: 'urgency', label: 'Degré urgence', type: 'select', options: [
        { value: 0, label: 'Électif' },
        { value: 1, label: 'Urgent' },
        { value: 2, label: 'Urgence' },
        { value: 3, label: 'Sauvetage' }
      ]}
    ],
    calculate: (values) => {
      let total = 0;
      const age = values.age || 60;
      if (age >= 60) total += Math.floor((age - 60) / 5);
      total += values.renal || 0;
      if (values.extracardiac) total += 1;
      if (values.poorMobility) total += 1;
      if (values.cardiac) total += 1;
      if (values.pulmonary) total += 1;
      if (values.endocarditis) total += 1;
      if (values.critical) total += 2;
      total += values.lvef || 0;
      total += values.urgency || 0;
      
      // Estimation mortalité (simplifiée)
      const mortality = Math.min(1 + total * 1.5, 50);
      return { score: total, max: 20, mortality: mortality.toFixed(1) };
    },
    interpret: (score, calculated) => {
      const m = calculated?.mortality || 1;
      if (score <= 2) return { level: 'success', text: `EuroSCORE II ~${m}%`, detail: 'Faible risque opératoire' };
      if (score <= 5) return { level: 'warning', text: `EuroSCORE II ~${m}%`, detail: 'Risque modéré' };
      return { level: 'danger', text: `EuroSCORE II ~${m}%`, detail: 'Haut risque - Discuter alternative (TAVI, etc.)' };
    },
    formula: 'EuroSCORE II: version simplifiée. Calcul complet sur euroscore.org'
  },

  // FOUR Score - Coma
  four: {
    name: 'Score FOUR',
    category: 'Neurologie',
    icon: Brain,
    description: 'Alternative au Glasgow (coma)',
    color: 'purple',
    fields: [
      { id: 'eye', label: 'Réponse oculaire', type: 'select', options: [
        { value: 4, label: 'E4 - Paup. ouvertes, suit du regard, cligne sur demande' },
        { value: 3, label: 'E3 - Paup. ouvertes mais pas de poursuite' },
        { value: 2, label: 'E2 - Paup. fermées, s\'ouvrent à la voix' },
        { value: 1, label: 'E1 - Paup. fermées, s\'ouvrent à la douleur' },
        { value: 0, label: 'E0 - Pas d\'ouverture à la douleur' }
      ]},
      { id: 'motor', label: 'Réponse motrice', type: 'select', options: [
        { value: 4, label: 'M4 - Pouces levés, poing, signe de paix' },
        { value: 3, label: 'M3 - Localise la douleur' },
        { value: 2, label: 'M2 - Flexion à la douleur' },
        { value: 1, label: 'M1 - Extension à la douleur' },
        { value: 0, label: 'M0 - Pas de réponse ou myoclonies généralisées' }
      ]},
      { id: 'brainstem', label: 'Réflexes du tronc', type: 'select', options: [
        { value: 4, label: 'B4 - Réflexes pupillaire et cornéen présents' },
        { value: 3, label: 'B3 - Une pupille fixe dilatée' },
        { value: 2, label: 'B2 - Réflexe pupillaire OU cornéen absent' },
        { value: 1, label: 'B1 - Réflexes pupillaire ET cornéen absents' },
        { value: 0, label: 'B0 - Pupillaire, cornéen et toux absents' }
      ]},
      { id: 'respiration', label: 'Respiration', type: 'select', options: [
        { value: 4, label: 'R4 - Non intubé, respiration régulière' },
        { value: 3, label: 'R3 - Non intubé, Cheyne-Stokes' },
        { value: 2, label: 'R2 - Non intubé, respiration irrégulière' },
        { value: 1, label: 'R1 - Intubé, déclenche le ventilateur' },
        { value: 0, label: 'R0 - Intubé, apnée ou ne déclenche pas' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 16 };
    },
    interpret: (score) => {
      if (score >= 12) return { level: 'success', text: `FOUR ${score}/16`, detail: 'Atteinte légère de conscience' };
      if (score >= 8) return { level: 'warning', text: `FOUR ${score}/16`, detail: 'Atteinte modérée' };
      if (score >= 4) return { level: 'danger', text: `FOUR ${score}/16`, detail: 'Atteinte sévère' };
      return { level: 'danger', text: `FOUR ${score}/16`, detail: 'Pronostic très sombre - Évoquer mort encéphalique' };
    },
    formula: 'FOUR = Eye + Motor + Brainstem + Respiration (0-16)'
  },

  // QTc
  qtc: {
    name: 'QTc (Bazett)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'QT corrigé pour la fréquence cardiaque',
    color: 'rose',
    fields: [
      { id: 'qt', label: 'Intervalle QT mesuré (ms)', type: 'number', min: 200, max: 700 },
      { id: 'rr', label: 'Intervalle RR (ms)', type: 'number', min: 400, max: 2000 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' },
        { value: 'F', label: 'Femme' }
      ]}
    ],
    calculate: (values) => {
      const qt = values.qt || 400;
      const rr = values.rr || 800;
      const qtc = qt / Math.sqrt(rr / 1000);
      return { score: Math.round(qtc), max: '', unit: 'ms', sex: values.sex || 'M' };
    },
    interpret: (score, calculated) => {
      const sex = calculated?.sex || 'M';
      const upper = sex === 'M' ? 450 : 460;
      const danger = sex === 'M' ? 500 : 500;
      
      if (score < upper) return { level: 'success', text: `QTc ${score} ms`, detail: 'QTc normal' };
      if (score < danger) return { level: 'warning', text: `QTc ${score} ms`, detail: 'QTc allongé - Recherche étiologie, éviter médicaments allongeant QT' };
      return { level: 'danger', text: `QTc ${score} ms`, detail: 'QTc très allongé - Risque torsade de pointe' };
    },
    formula: 'QTc (Bazett) = QT / √RR. Normal H<450ms, F<460ms'
  },

  // Rockall - Hémorragie digestive
  rockall: {
    name: 'Score de Rockall',
    category: 'Hépato-Gastro',
    icon: Droplets,
    description: 'Pronostic hémorragie digestive haute',
    color: 'yellow',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 60 ans (0 pt)' },
        { value: 1, label: '60-79 ans (1 pt)' },
        { value: 2, label: '≥ 80 ans (2 pts)' }
      ]},
      { id: 'shock', label: 'Hémodynamique', type: 'select', options: [
        { value: 0, label: 'PAS ≥100, FC <100 (0 pt)' },
        { value: 1, label: 'PAS ≥100, FC ≥100 (1 pt)' },
        { value: 2, label: 'PAS <100 (2 pts)' }
      ]},
      { id: 'comorbidity', label: 'Comorbidités', type: 'select', options: [
        { value: 0, label: 'Aucune majeure (0 pt)' },
        { value: 2, label: 'IC, cardiopathie ischémique, autre majeure (2 pts)' },
        { value: 3, label: 'IRC, IH, néoplasie disséminée (3 pts)' }
      ]},
      { id: 'diagnosis', label: 'Diagnostic endoscopique', type: 'select', options: [
        { value: 0, label: 'Mallory-Weiss, pas de lésion, pas de stigmate (0 pt)' },
        { value: 1, label: 'Autre diagnostic (1 pt)' },
        { value: 2, label: 'Néoplasie digestive haute (2 pts)' }
      ]},
      { id: 'stigmata', label: 'Stigmates hémorragie', type: 'select', options: [
        { value: 0, label: 'Absence ou spot noir (0 pt)' },
        { value: 2, label: 'Sang, caillot adhérent, vaisseau visible, saignement actif (2 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 11 };
    },
    interpret: (score) => {
      const mortality = { 0: 0.2, 1: 2.4, 2: 5.6, 3: 11, 4: 24.6, 5: 39.6, 6: 48.9, 7: 50 };
      const mort = mortality[Math.min(score, 7)] || '>50';
      if (score <= 2) return { level: 'success', text: `Rockall ${score}`, detail: `Mortalité ~${mort}% - Sortie précoce possible` };
      if (score <= 4) return { level: 'warning', text: `Rockall ${score}`, detail: `Mortalité ~${mort}%` };
      return { level: 'danger', text: `Rockall ${score}`, detail: `Mortalité ~${mort}% - Haut risque` };
    },
    formula: 'Rockall = Âge + Choc + Comorbidité + Diagnostic + Stigmates'
  },

  // Stop-Bang - SAOS
  stopBang: {
    name: 'Score STOP-BANG',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Dépistage apnée du sommeil',
    color: 'sky',
    fields: [
      { id: 'snoring', label: 'S - Ronflement fort', type: 'checkbox', points: 1 },
      { id: 'tired', label: 'T - Fatigue diurne', type: 'checkbox', points: 1 },
      { id: 'observed', label: 'O - Apnées observées', type: 'checkbox', points: 1 },
      { id: 'pressure', label: 'P - Pression artérielle élevée/traitée', type: 'checkbox', points: 1 },
      { id: 'bmi', label: 'B - IMC > 35', type: 'checkbox', points: 1 },
      { id: 'age', label: 'A - Âge > 50 ans', type: 'checkbox', points: 1 },
      { id: 'neck', label: 'N - Tour de cou > 40cm (H) ou 38cm (F)', type: 'checkbox', points: 1 },
      { id: 'gender', label: 'G - Sexe masculin', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `STOP-BANG ${score}`, detail: 'Faible risque SAOS' };
      if (score <= 4) return { level: 'warning', text: `STOP-BANG ${score}`, detail: 'Risque intermédiaire - Polygraphie à discuter' };
      return { level: 'danger', text: `STOP-BANG ${score}`, detail: 'Haut risque SAOS - Polygraphie recommandée' };
    },
    formula: 'STOP-BANG: 8 items. ≥3 = risque modéré, ≥5 = risque élevé'
  }
};
